/**
 * Category List Table Component
 * Table for displaying and managing adventure categories
 */

import { useState } from 'react'
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  IconButton,
  Button,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material'
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Leaderboard as LeaderboardIcon,
} from '@mui/icons-material'
import { useAdventureCategories, useDeleteCategory } from '../../hooks/useAdventureCategories'
import { CategoryFormDialog } from './CategoryFormDialog'
import type { AdventureCategoryDto } from '@api/types/adventureCategory'

export function CategoryListTable() {
  const [showInactive, setShowInactive] = useState(false)
  const [formDialogOpen, setFormDialogOpen] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState<AdventureCategoryDto | null>(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [categoryToDelete, setCategoryToDelete] = useState<AdventureCategoryDto | null>(null)

  const { data: categories, isLoading, error, refetch } = useAdventureCategories(showInactive)
  const deleteMutation = useDeleteCategory()

  const handleCreateClick = () => {
    setSelectedCategory(null)
    setFormDialogOpen(true)
  }

  const handleEditClick = (category: AdventureCategoryDto) => {
    setSelectedCategory(category)
    setFormDialogOpen(true)
  }

  const handleDeleteClick = (category: AdventureCategoryDto) => {
    setCategoryToDelete(category)
    setDeleteDialogOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (!categoryToDelete) return

    try {
      await deleteMutation.mutateAsync(categoryToDelete.id)
      setDeleteDialogOpen(false)
      setCategoryToDelete(null)
    } catch (err) {
      // Error is handled by the mutation
    }
  }

  const getFeatureChips = (category: AdventureCategoryDto) => {
    const chips: { label: string; enabled: boolean | null; variant?: 'filled' | 'outlined' }[] = [
      { label: 'Workflow', enabled: category.workflowAllowed },
      { label: 'Required WF', enabled: category.workflowRequired },
      { label: 'Public', enabled: category.isPublicAllowed, variant: category.isPublicAllowed === null ? 'outlined' : 'filled' },
      { label: 'Groups', enabled: category.groupsAllowed, variant: category.groupsAllowed === null ? 'outlined' : 'filled' },
      { label: 'Repeatable', enabled: category.repeatableAllowed, variant: category.repeatableAllowed === null ? 'outlined' : 'filled' },
    ]

    // Show enabled features, and null values as "inherit" with outlined variant
    return chips.filter((c) => c.enabled === true || c.enabled === null).map((c) => ({
      label: c.enabled === null ? `${c.label}?` : c.label,
      variant: c.variant || 'filled',
    }))
  }

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
              />
            }
            label="Show Inactive"
          />
          <Tooltip title="Refresh">
            <IconButton onClick={() => refetch()} disabled={isLoading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreateClick}>
          Add Category
        </Button>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error instanceof Error ? error.message : 'Failed to load categories'}
        </Alert>
      )}

      {deleteMutation.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {deleteMutation.error instanceof Error
            ? deleteMutation.error.message
            : 'Failed to delete category'}
        </Alert>
      )}

      {/* Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'primary.main' }}>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Icon/Color</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Name</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Display Name</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Features</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Adventures</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Sort Order</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : !categories || categories.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No categories found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                categories.map((category) => (
                  <TableRow
                    key={category.id}
                    sx={{
                      '&:hover': { backgroundColor: 'action.hover' },
                      opacity: category.isActive ? 1 : 0.6,
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {category.iconName && (
                          <Typography variant="h6">{category.iconName}</Typography>
                        )}
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: 1,
                            backgroundColor: category.color || '#ccc',
                            border: '1px solid rgba(0,0,0,0.1)',
                          }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {category.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{category.displayName}</Typography>
                      {category.description && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            display: 'block',
                            maxWidth: 200,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {category.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                        {category.hasLeaderboard && (
                          <Tooltip title="Has Leaderboard">
                            <LeaderboardIcon fontSize="small" color="primary" />
                          </Tooltip>
                        )}
                        {getFeatureChips(category).map(({ label, variant }) => (
                          <Chip
                            key={label}
                            label={label}
                            size="small"
                            variant={variant as 'filled' | 'outlined'}
                            sx={{ fontSize: '0.7rem' }}
                          />
                        ))}
                        {getFeatureChips(category).length === 0 && !category.hasLeaderboard && (
                          <Typography variant="caption" color="text.secondary">
                            None
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={category.isActive ? 'Active' : 'Inactive'}
                        color={category.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{category.adventureCount}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{category.sortOrder}</Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => handleEditClick(category)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteClick(category)}
                            disabled={deleteMutation.isPending}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Form Dialog */}
      <CategoryFormDialog
        open={formDialogOpen}
        onClose={() => {
          setFormDialogOpen(false)
          setSelectedCategory(null)
        }}
        category={selectedCategory}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false)
          setCategoryToDelete(null)
        }}
      >
        <DialogTitle>Delete Category</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the category "{categoryToDelete?.displayName}"?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteDialogOpen(false)
              setCategoryToDelete(null)
            }}
            disabled={deleteMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            color="error"
            onClick={handleDeleteConfirm}
            disabled={deleteMutation.isPending}
            startIcon={deleteMutation.isPending ? <CircularProgress size={20} /> : null}
          >
            {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
